import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/utils/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import ProjectMultiSelect from '@/components/ProjectMultiSelect';
import LocationMultiSelect from '@/components/LocationMultiSelect';
import { 
  ArrowLeft, ArrowRight, User, Building2, Check,
  Calendar, Clock, MapPin, Home, X, AlertCircle, Users
} from 'lucide-react';
import { toast } from 'sonner';
import { MobileMenuTrigger } from '@/components/Layout';

// Visit Source Options
const VISIT_SOURCES = [
  { value: "Inside Sales", label: "Inside Sales" },
  { value: "Customer Referral", label: "Customer Referral" },
  { value: "Employee Referral", label: "Employee Referral" },
  { value: "External Database", label: "External Database" },
  { value: "Rep Self Generated", label: "Rep Self Generated" }
];

const AddCustomer = ({ user }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [projectsError, setProjectsError] = useState(false);
  const [budgetError, setBudgetError] = useState('');
  
  // Dynamic locations based on user's city
  const [cityLocations, setCityLocations] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const userCity = user?.city || '';
  
  // Step 1: Basic Details
  const [basicDetails, setBasicDetails] = useState({
    customer_name: '',
    customer_email: '',
    budget_min_lacs: '',
    budget_max_lacs: '',
    preferred_locations: [],
    configuration: '',
    purpose: 'End-use',
    notes: '',
  });
  
  // Step 2: First Visit
  const [visitDetails, setVisitDetails] = useState({
    visit_date: new Date().toISOString().split('T')[0],
    visit_time: new Date().toTimeString().slice(0, 5),
    selected_projects: [],
    remarks: '',
    next_followup_date: '',
    next_followup_time: '',
    visit_source: '',
    visit_source_detail: '',
  });

  // Referral Tracking
  const [isReferred, setIsReferred] = useState(false);
  const [referralDetails, setReferralDetails] = useState({
    referrer_type: '', // 'customer' or 'employee'
    referrer_customer_id: '',
    referrer_employee_email: '',
  });
  const [referrerSearch, setReferrerSearch] = useState('');
  const [referrerResults, setReferrerResults] = useState([]);
  const [selectedReferrer, setSelectedReferrer] = useState(null);
  const [referrerSearching, setReferrerSearching] = useState(false);

  // Fetch locations for user's city
  useEffect(() => {
    const fetchLocations = async () => {
      if (!userCity) {
        setCityLocations([]);
        return;
      }
      setLocationsLoading(true);
      try {
        const response = await api.getLocationsByCity(userCity);
        const locations = response.data?.locations || [];
        setCityLocations(locations);
      } catch (error) {
        console.error('Failed to load locations for city:', error);
        setCityLocations([]);
      } finally {
        setLocationsLoading(false);
      }
    };
    fetchLocations();
  }, [userCity]);

  useEffect(() => {
    fetchProjects();
  }, []);

  // Validate budget in real-time
  useEffect(() => {
    const min = parseFloat(basicDetails.budget_min_lacs) || 0;
    const max = parseFloat(basicDetails.budget_max_lacs) || 0;
    if (min > 0 && max > 0 && min > max) {
      setBudgetError('Min budget cannot exceed max budget');
    } else {
      setBudgetError('');
    }
  }, [basicDetails.budget_min_lacs, basicDetails.budget_max_lacs]);

  const fetchProjects = async () => {
    try {
      // Fetch initial batch of projects (for display before search)
      const response = await api.getActiveProjects({ city: userCity });
      const projectsData = response.data?.projects || response.data || [];
      setProjects(Array.isArray(projectsData) ? projectsData : []);
    } catch (error) {
      console.error('Failed to load projects');
      setProjects([]);
    }
  };

  const formatCurrencyInput = (value) => {
    // Remove non-numeric characters except decimal
    return value.replace(/[^0-9.]/g, '');
  };

  const validateStep1 = () => {
    if (!basicDetails.customer_name.trim()) {
      toast.error('Customer name is required');
      return false;
    }
    if (!basicDetails.customer_email.trim()) {
      toast.error('Email is required');
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(basicDetails.customer_email)) {
      toast.error('Please enter a valid email');
      return false;
    }
    if (budgetError) {
      toast.error(budgetError);
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    let isValid = true;
    
    if (!visitDetails.visit_date) {
      toast.error('Visit date is required');
      isValid = false;
    }
    if (!visitDetails.visit_time) {
      toast.error('Visit time is required');
      isValid = false;
    }
    if (visitDetails.selected_projects.length === 0) {
      setProjectsError(true);
      toast.error('Please select at least one project');
      isValid = false;
    } else {
      setProjectsError(false);
    }
    if (!visitDetails.remarks.trim()) {
      toast.error('Remarks are required');
      isValid = false;
    }
    if (!visitDetails.visit_source) {
      toast.error('Source of Visit is required');
      isValid = false;
    }
    if (visitDetails.visit_source === 'External Database' && !visitDetails.visit_source_detail.trim()) {
      toast.error('External source name is required');
      isValid = false;
    }
    return isValid;
  };

  const handleNext = () => {
    if (validateStep1()) {
      setStep(2);
    }
  };

  // Referrer search with debounce
  const searchReferrers = async (query) => {
    if (!query || query.trim().length < 2) {
      setReferrerResults([]);
      return;
    }
    setReferrerSearching(true);
    try {
      const response = await api.searchReferrerCustomers(query);
      setReferrerResults(response.data?.customers || []);
    } catch (error) {
      console.error('Referrer search failed:', error);
      setReferrerResults([]);
    } finally {
      setReferrerSearching(false);
    }
  };

  const handleReferrerSearch = (value) => {
    setReferrerSearch(value);
    if (value.length >= 2) {
      searchReferrers(value);
    } else {
      setReferrerResults([]);
    }
  };

  const selectReferrer = (customer) => {
    setSelectedReferrer(customer);
    setReferralDetails(prev => ({
      ...prev,
      referrer_customer_id: customer.customer_id,
    }));
    setReferrerSearch(customer.customer_name);
    setReferrerResults([]);
  };

  const validateReferral = () => {
    if (!isReferred) return true;
    
    if (!referralDetails.referrer_type) {
      toast.error('Please select referrer type');
      return false;
    }
    
    if (referralDetails.referrer_type === 'customer' && !referralDetails.referrer_customer_id) {
      toast.error('Please select a referrer customer');
      return false;
    }
    
    if (referralDetails.referrer_type === 'employee') {
      if (!referralDetails.referrer_employee_email) {
        toast.error('Please enter employee email');
        return false;
      }
      if (!referralDetails.referrer_employee_email.endsWith('@nobroker.in')) {
        toast.error('Employee email must be @nobroker.in domain');
        return false;
      }
    }
    
    return true;
  };

  const handleSubmit = async () => {
    if (!validateStep2()) return;
    if (!validateReferral()) return;

    setLoading(true);
    try {
      let nextFollowup = null;
      if (visitDetails.next_followup_date) {
        const time = visitDetails.next_followup_time || '10:00';
        nextFollowup = `${visitDetails.next_followup_date}T${time}:00`;
      }
      
      const projectNames = visitDetails.selected_projects.map(pid => {
        const project = projects.find(p => p.project_id === pid);
        return project?.name || project?.project_name || '';
      });
      
      const data = {
        customer_name: basicDetails.customer_name.trim(),
        customer_email: basicDetails.customer_email.trim().toLowerCase(),
        budget_min_lacs: basicDetails.budget_min_lacs ? parseFloat(basicDetails.budget_min_lacs) : null,
        budget_max_lacs: basicDetails.budget_max_lacs ? parseFloat(basicDetails.budget_max_lacs) : null,
        preferred_locations: basicDetails.preferred_locations.length > 0 ? basicDetails.preferred_locations : null,
        configuration: basicDetails.configuration || null,
        purpose: basicDetails.purpose || "End-use",
        notes: basicDetails.notes || null,
        visit_date: visitDetails.visit_date,
        visit_time: visitDetails.visit_time,
        project_ids: visitDetails.selected_projects,
        project_names: projectNames,
        remarks: visitDetails.remarks.trim(),
        next_followup_datetime: nextFollowup,
        visit_source: visitDetails.visit_source,
        visit_source_detail: visitDetails.visit_source === 'External Database' ? visitDetails.visit_source_detail.trim() : null,
      };
      
      const response = await api.createCustomerWithVisit(data);
      const customerId = response.data?.customer_id;
      
      // If customer was referred, create the referral attribution
      if (isReferred && customerId) {
        try {
          const referralData = {
            referrer_type: referralDetails.referrer_type,
            referrer_customer_id: referralDetails.referrer_type === 'customer' ? referralDetails.referrer_customer_id : null,
            referrer_employee_email: referralDetails.referrer_type === 'employee' ? referralDetails.referrer_employee_email : null,
          };
          await api.createReferral(customerId, referralData);
          toast.success('Customer added with referral attribution!');
        } catch (refError) {
          console.error('Failed to create referral:', refError);
          toast.warning('Customer added but referral attribution failed. You can add it later from customer details.');
        }
      } else {
        toast.success('Customer added successfully!');
      }
      
      navigate('/crm');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to add customer';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleProjectSelection = (selectedIds) => {
    setProjectsError(false);
    setVisitDetails(prev => ({
      ...prev,
      selected_projects: selectedIds
    }));
  };

  // Server-side project search function
  const handleProjectSearch = async (query) => {
    if (!query || query.trim().length < 2) return [];
    try {
      const response = await api.searchProjects(query, userCity, 50);
      return response.data?.projects || [];
    } catch (error) {
      console.error('Project search failed:', error);
      return [];
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F8FA]">
      {/* Compact Header */}
      <div className="bg-white border-b border-slate-200 px-4 py-3 sticky top-0 z-10 shadow-sm">
        <div className="flex items-center gap-3 max-w-lg mx-auto">
          <button 
            onClick={() => navigate('/')} 
            className="p-2 hover:bg-slate-100 rounded-xl transition-colors"
            data-testid="home-btn"
          >
            <Home size={20} className="text-slate-600" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold text-[#111827]">Add Customer</h1>
            <p className="text-xs text-slate-400">Step {step} of 2</p>
          </div>
          <MobileMenuTrigger user={user} />
        </div>
        
        {/* Thicker Progress Bar */}
        <div className="flex gap-2 mt-3 max-w-lg mx-auto">
          <div className={`h-1.5 flex-1 rounded-full transition-colors ${step >= 1 ? 'bg-[#5B3DF5]' : 'bg-slate-200'}`} />
          <div className={`h-1.5 flex-1 rounded-full transition-colors ${step >= 2 ? 'bg-[#5B3DF5]' : 'bg-slate-200'}`} />
        </div>
      </div>

      <div className="p-4 max-w-lg mx-auto pb-24">
        {step === 1 ? (
          /* Step 1: Basic Details */
          <div className="space-y-3">
            <Card className="p-4 bg-white shadow-sm rounded-2xl">
              <div className="flex items-center gap-2 mb-3">
                <User size={18} className="text-[#5B3DF5]" />
                <h2 className="font-bold text-[#111827]">Basic Details</h2>
              </div>
              
              <div className="space-y-3">
                <div>
                  <Label htmlFor="name" className="text-xs font-semibold text-slate-600">Customer Name *</Label>
                  <Input
                    id="name"
                    data-testid="customer-name-input"
                    value={basicDetails.customer_name}
                    onChange={(e) => setBasicDetails({...basicDetails, customer_name: e.target.value})}
                    placeholder="Enter full name"
                    className="mt-1 h-11 rounded-xl"
                  />
                </div>
                
                <div>
                  <Label htmlFor="email" className="text-xs font-semibold text-slate-600">Email (Unique ID) *</Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="customer-email-input"
                    value={basicDetails.customer_email}
                    onChange={(e) => setBasicDetails({...basicDetails, customer_email: e.target.value})}
                    placeholder="customer@email.com"
                    className="mt-1 h-11 rounded-xl"
                  />
                </div>
              </div>
            </Card>

            <Card className="p-4 bg-white shadow-sm rounded-2xl">
              <div className="flex items-center gap-2 mb-3">
                <Home size={18} className="text-[#5B3DF5]" />
                <h2 className="font-bold text-[#111827]">Requirements</h2>
              </div>
              
              <div className="space-y-3">
                {/* Budget with ₹ symbol */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="budget_min" className="text-xs font-semibold text-slate-600">Budget Min</Label>
                    <div className="relative mt-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-medium">₹</span>
                      <Input
                        id="budget_min"
                        type="text"
                        inputMode="numeric"
                        value={basicDetails.budget_min_lacs}
                        onChange={(e) => setBasicDetails({...basicDetails, budget_min_lacs: formatCurrencyInput(e.target.value)})}
                        placeholder="50"
                        className="pl-7 pr-8 h-11 rounded-xl"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs">Lacs</span>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="budget_max" className="text-xs font-semibold text-slate-600">Budget Max</Label>
                    <div className="relative mt-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-medium">₹</span>
                      <Input
                        id="budget_max"
                        type="text"
                        inputMode="numeric"
                        value={basicDetails.budget_max_lacs}
                        onChange={(e) => setBasicDetails({...basicDetails, budget_max_lacs: formatCurrencyInput(e.target.value)})}
                        placeholder="100"
                        className="pl-7 pr-8 h-11 rounded-xl"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs">Lacs</span>
                    </div>
                  </div>
                </div>
                
                {/* Budget Validation Error */}
                {budgetError && (
                  <div className="flex items-center gap-1 text-[#5B3DF5] text-xs">
                    <AlertCircle size={12} />
                    <span>{budgetError}</span>
                  </div>
                )}
                
                {/* Preferred Locations - Searchable Multi-Select */}
                <div>
                  <Label className="text-xs font-semibold text-slate-600">
                    Preferred Locations {userCity && <span className="text-slate-400 font-normal">({userCity})</span>}
                  </Label>
                  {!userCity ? (
                    <p className="text-xs text-amber-600 mt-2">City not set in your profile</p>
                  ) : (
                    <div className="mt-2">
                      <LocationMultiSelect
                        locations={cityLocations}
                        selectedLocations={basicDetails.preferred_locations}
                        onChange={(newLocations) => setBasicDetails({...basicDetails, preferred_locations: newLocations})}
                        cityName={userCity}
                        isLoading={locationsLoading}
                        maxSelections={10}
                        placeholder="Search locations..."
                      />
                    </div>
                  )}
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-semibold text-slate-600">Configuration</Label>
                    <Select 
                      value={basicDetails.configuration} 
                      onValueChange={(v) => setBasicDetails({...basicDetails, configuration: v})}
                    >
                      <SelectTrigger className="mt-1 h-11 rounded-xl">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1BHK">1 BHK</SelectItem>
                        <SelectItem value="2BHK">2 BHK</SelectItem>
                        <SelectItem value="3BHK">3 BHK</SelectItem>
                        <SelectItem value="4BHK+">4 BHK+</SelectItem>
                        <SelectItem value="Villa">Villa</SelectItem>
                        <SelectItem value="Penthouse">Penthouse</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-600">Purpose</Label>
                    <Select 
                      value={basicDetails.purpose} 
                      onValueChange={(v) => setBasicDetails({...basicDetails, purpose: v})}
                    >
                      <SelectTrigger className="mt-1 h-11 rounded-xl">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="End-use">End-use</SelectItem>
                        <SelectItem value="Investment">Investment</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="notes" className="text-xs font-semibold text-slate-600">Notes</Label>
                  <Textarea
                    id="notes"
                    value={basicDetails.notes}
                    onChange={(e) => setBasicDetails({...basicDetails, notes: e.target.value})}
                    placeholder="Any additional notes..."
                    rows={2}
                    className="mt-1 rounded-xl"
                  />
                </div>
              </div>
            </Card>

            {/* Referral Section */}
            <Card className="p-4 bg-white shadow-sm rounded-2xl">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Users size={18} className="text-purple-500" />
                  <h2 className="font-bold text-[#111827]">Referral</h2>
                </div>
                <button
                  type="button"
                  data-testid="referral-toggle"
                  onClick={() => setIsReferred(!isReferred)}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${isReferred ? 'bg-purple-600' : 'bg-slate-200'}`}
                >
                  <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${isReferred ? 'translate-x-5' : 'translate-x-0'}`} />
                </button>
              </div>
              
              <p className="text-xs text-slate-500 mb-3">Was this customer referred?</p>
              
              {isReferred && (
                <div className="space-y-3 mt-3 pt-3 border-t border-slate-100">
                  {/* Referrer Type */}
                  <div>
                    <Label className="text-xs font-semibold text-slate-600">Referrer Type *</Label>
                    <Select 
                      value={referralDetails.referrer_type} 
                      onValueChange={(v) => {
                        setReferralDetails({...referralDetails, referrer_type: v, referrer_customer_id: '', referrer_employee_email: ''});
                        setSelectedReferrer(null);
                        setReferrerSearch('');
                      }}
                    >
                      <SelectTrigger data-testid="referrer-type-select" className="mt-1 h-11 rounded-xl">
                        <SelectValue placeholder="Select referrer type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="customer">Customer Referral</SelectItem>
                        <SelectItem value="employee">Employee Referral</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Customer Referrer Search */}
                  {referralDetails.referrer_type === 'customer' && (
                    <div>
                      <Label className="text-xs font-semibold text-slate-600">Search Referrer Customer *</Label>
                      <div className="relative mt-1">
                        <Input
                          data-testid="referrer-customer-search"
                          value={referrerSearch}
                          onChange={(e) => handleReferrerSearch(e.target.value)}
                          placeholder="Search by name or email..."
                          className="h-11 rounded-xl"
                        />
                        {referrerSearching && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            <div className="animate-spin h-4 w-4 border-2 border-purple-500 border-t-transparent rounded-full" />
                          </div>
                        )}
                        
                        {/* Search Results Dropdown */}
                        {referrerResults.length > 0 && (
                          <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                            {referrerResults.map((customer) => (
                              <button
                                key={customer.customer_id}
                                type="button"
                                onClick={() => selectReferrer(customer)}
                                className="w-full px-3 py-2 text-left hover:bg-slate-50 flex items-center gap-2"
                              >
                                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 text-xs font-bold">
                                  {customer.customer_name?.charAt(0).toUpperCase()}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-slate-900 truncate">{customer.customer_name}</p>
                                  <p className="text-xs text-slate-500 truncate">{customer.customer_email}</p>
                                </div>
                                <span className={`text-[10px] px-2 py-0.5 rounded-full ${customer.stage === 'Won' ? 'bg-emerald-100 text-emerald-700' : customer.stage === 'Booking' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'}`}>
                                  {customer.stage}
                                </span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      {/* Selected Referrer Display */}
                      {selectedReferrer && (
                        <div className="mt-2 p-2 bg-purple-50 rounded-lg flex items-center gap-2">
                          <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 text-xs font-bold">
                            {selectedReferrer.customer_name?.charAt(0).toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-purple-900 truncate">{selectedReferrer.customer_name}</p>
                            <p className="text-xs text-purple-600 truncate">{selectedReferrer.customer_email}</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedReferrer(null);
                              setReferralDetails({...referralDetails, referrer_customer_id: ''});
                              setReferrerSearch('');
                            }}
                            className="p-1 hover:bg-purple-100 rounded-full"
                          >
                            <X size={14} className="text-purple-500" />
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Employee Email Input */}
                  {referralDetails.referrer_type === 'employee' && (
                    <div>
                      <Label className="text-xs font-semibold text-slate-600">Employee Email *</Label>
                      <Input
                        data-testid="referrer-employee-email"
                        type="email"
                        value={referralDetails.referrer_employee_email}
                        onChange={(e) => setReferralDetails({...referralDetails, referrer_employee_email: e.target.value})}
                        placeholder="employee@nobroker.in"
                        className="mt-1 h-11 rounded-xl"
                      />
                      <p className="text-[10px] text-slate-400 mt-1">Must be @nobroker.in domain</p>
                    </div>
                  )}
                  
                  {/* Benefit Info */}
                  <div className="p-2 bg-amber-50 rounded-lg flex items-center gap-2">
                    <AlertCircle size={14} className="text-amber-500 flex-shrink-0" />
                    <p className="text-xs text-amber-700">
                      Benefit: <span className="font-semibold">0.5% of Agreement Value</span> (eligible after Agreement Done)
                    </p>
                  </div>
                </div>
              )}
            </Card>
          </div>
        ) : (
          /* Step 2: First Site Visit */
          <div className="space-y-3">
            <Card className="p-4 bg-white shadow-sm rounded-2xl">
              <div className="flex items-center gap-2 mb-3">
                <Calendar size={18} className="text-[#5B3DF5]" />
                <h2 className="font-bold text-[#111827]">First Site Visit</h2>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Visit Date *</Label>
                  <Input
                    type="date"
                    data-testid="visit-date-input"
                    value={visitDetails.visit_date}
                    onChange={(e) => setVisitDetails({...visitDetails, visit_date: e.target.value})}
                    className="mt-1 h-11 rounded-xl"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Visit Time *</Label>
                  <Input
                    type="time"
                    data-testid="visit-time-input"
                    value={visitDetails.visit_time}
                    onChange={(e) => setVisitDetails({...visitDetails, visit_time: e.target.value})}
                    className="mt-1 h-11 rounded-xl"
                  />
                </div>
              </div>
            </Card>

            <Card className="p-4 bg-white shadow-sm rounded-2xl">
              <div className="flex items-center gap-2 mb-2">
                <Building2 size={18} className="text-[#5B3DF5]" />
                <h2 className="font-bold text-[#111827]">Projects Shown *</h2>
              </div>
              
              <ProjectMultiSelect
                projects={projects}
                selectedIds={visitDetails.selected_projects}
                onChange={handleProjectSelection}
                onSearch={handleProjectSearch}
                placeholder="Search and select projects shown"
                error={projectsError}
              />
            </Card>

            <Card className="p-4 bg-white shadow-sm rounded-2xl">
              <div>
                <Label className="text-xs font-semibold text-slate-600">Remarks *</Label>
                <Textarea
                  data-testid="visit-remarks-input"
                  value={visitDetails.remarks}
                  onChange={(e) => setVisitDetails({...visitDetails, remarks: e.target.value})}
                  placeholder="Customer feedback, interest level, observations..."
                  rows={3}
                  className="mt-1 rounded-xl"
                />
              </div>
            </Card>

            <Card className="p-4 bg-white shadow-sm rounded-2xl">
              <div className="flex items-center gap-2 mb-3">
                <Users size={18} className="text-[#5B3DF5]" />
                <h2 className="font-bold text-[#111827]">Source of Visit *</h2>
              </div>
              
              <div className="space-y-3">
                <div>
                  <Label className="text-xs font-semibold text-slate-600">How did this customer reach you? *</Label>
                  <Select 
                    value={visitDetails.visit_source} 
                    onValueChange={(v) => setVisitDetails({...visitDetails, visit_source: v, visit_source_detail: v !== 'External Database' ? '' : visitDetails.visit_source_detail})}
                  >
                    <SelectTrigger data-testid="visit-source-select" className="mt-1 h-11 rounded-xl">
                      <SelectValue placeholder="Select source" />
                    </SelectTrigger>
                    <SelectContent>
                      {VISIT_SOURCES.map((source) => (
                        <SelectItem key={source.value} value={source.value}>{source.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {visitDetails.visit_source === 'External Database' && (
                  <div>
                    <Label className="text-xs font-semibold text-slate-600">External Source Name *</Label>
                    <Input
                      data-testid="visit-source-detail-input"
                      value={visitDetails.visit_source_detail}
                      onChange={(e) => setVisitDetails({...visitDetails, visit_source_detail: e.target.value})}
                      placeholder="e.g., Housing.com, 99acres, MagicBricks"
                      className="mt-1 h-11 rounded-xl"
                    />
                  </div>
                )}
              </div>
            </Card>

            <Card className="p-4 bg-white shadow-sm rounded-2xl">
              <div className="flex items-center gap-2 mb-3">
                <Clock size={18} className="text-slate-400" />
                <h2 className="font-semibold text-slate-600 text-sm">Schedule Follow-up (Optional)</h2>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Date</Label>
                  <Input
                    type="date"
                    value={visitDetails.next_followup_date}
                    onChange={(e) => setVisitDetails({...visitDetails, next_followup_date: e.target.value})}
                    className="mt-1 h-11 rounded-xl"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Time</Label>
                  <Input
                    type="time"
                    value={visitDetails.next_followup_time}
                    onChange={(e) => setVisitDetails({...visitDetails, next_followup_time: e.target.value})}
                    className="mt-1 h-11 rounded-xl"
                  />
                </div>
              </div>
            </Card>
          </div>
        )}
      </div>

      {/* Sticky Bottom CTA - with padding for mobile nav */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-4 pb-20 md:pb-4 shadow-lg z-50">
        <div className="max-w-lg mx-auto">
          {step === 1 ? (
            <Button
              data-testid="next-step-button"
              onClick={handleNext}
              className="w-full bg-[#5B3DF5] hover:bg-[#4A2FE0] h-12 rounded-full font-bold"
            >
              Next: Add First Visit
              <ArrowRight size={18} className="ml-2" />
            </Button>
          ) : (
            <Button
              data-testid="submit-customer-button"
              onClick={handleSubmit}
              disabled={loading}
              className="w-full bg-[#5B3DF5] hover:bg-[#4A2FE0] h-12 rounded-full font-bold"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2" />
                  Creating...
                </>
              ) : (
                <>
                  <Check size={18} className="mr-2" />
                  Add Customer
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddCustomer;
